import styled, { createGlobalStyle } from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueMui,
  greenMui,
  greyMui,
  orangeMui,
  redMui,
  rydeColors,
  ScreenDepth,
} from "@ryde/theme";

import {
  MAP_VEHICLE_TRACKING_POPUP_CLASS,
  MAP_VEHICLE_TRACKING_POPUP_MAX_WIDTH_PX,
  MAP_VEHICLE_TRACKING_POPUP_MIN_WIDTH_PX,
} from "./map-tooltip.constants";

/** Leaflet popup chrome for vehicle tracking tooltip. */
export const MapVehicleTrackingPopupGlobalStyle = createGlobalStyle`
    .detail-row
    > [data-e2e="framer-tooltip"] {
    display: block;
    grid-column: 2;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
  }

  /* MapLibre twin of the Leaflet vehicle-tracking popup chrome. */
  .maplibregl-popup.${MAP_VEHICLE_TRACKING_POPUP_CLASS} {
    z-index: ${ScreenDepth.MAP_VEHICLE_TRACKING_POPUP} !important;
    max-width: ${MAP_VEHICLE_TRACKING_POPUP_MAX_WIDTH_PX}px !important;
  }

  .maplibregl-popup.${MAP_VEHICLE_TRACKING_POPUP_CLASS} .maplibregl-popup-content {
    box-sizing: border-box;
    padding: 0;
    margin: 0;
    min-width: ${MAP_VEHICLE_TRACKING_POPUP_MIN_WIDTH_PX}px;
    max-width: ${MAP_VEHICLE_TRACKING_POPUP_MAX_WIDTH_PX}px;
    width: max-content;
    border-radius: 8px;
    border: 1px solid ${greyMui._300};
    box-shadow:
      0 10px 28px ${rydeColors.black}2e,
      0 4px 12px ${rydeColors.black}1a;
    line-height: 1.35;
    overflow: hidden;
  }

  .maplibregl-popup.${MAP_VEHICLE_TRACKING_POPUP_CLASS}
    .detail-row
    > [data-e2e="framer-tooltip"] {
    display: block;
    grid-column: 2;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
  }

  .maplibregl-popup.${MAP_VEHICLE_TRACKING_POPUP_CLASS} .maplibregl-popup-close-button {
    top: 6px !important;
    right: 6px !important;
    left: auto !important;
    inset-inline-end: 6px;
    inset-inline-start: auto;
    width: 22px;
    height: 22px;
    font-size: 18px;
    line-height: 22px;
    color: ${greyMui._600};
    border-radius: 4px;
    background: transparent;
    border: none;
    padding: 0;
    z-index: 2;

    &:hover {
      color: ${rydeColors.black};
      background: ${greyMui._100};
    }
  }

  html[dir="rtl"] .maplibregl-popup.${MAP_VEHICLE_TRACKING_POPUP_CLASS} .maplibregl-popup-close-button {
    right: auto !important;
    left: 6px !important;
  }
`;

export const MapTooltipStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "map-vehicle-tracking-tooltip",
})`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow-x: clip;
  font-family: inherit;
  color: ${rydeColors.black};
  /* End padding clears the absolute close button; keep start aligned with body. */
  padding: 12px 14px 14px;
  padding-inline-end: 28px;

  .time-strip {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) minmax(0, 1fr);
    align-items: end;
    column-gap: 8px;
    row-gap: 6px;
    padding-bottom: 12px;
    margin-bottom: 12px;
    border-bottom: 1px solid ${greyMui._200};
  }

  .time-strip__arrow {
    flex-shrink: 0;
    font-size: 16px;
    line-height: 1;
    padding-bottom: 2px;
    opacity: 0.85;
    justify-self: center;
  }

  html[dir="rtl"] & .time-strip__arrow {
    transform: scaleX(-1);
  }

  .time-cell {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .time-cell__label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    text-align: start;
    color: ${greyMui._600};
  }

  .time-cell__value {
    font-size: 16px;
    font-weight: 600;
    line-height: 1.2;
    text-align: start;
    color: ${rydeColors.black};
    font-variant-numeric: tabular-nums;
  }

  .time-cell__value--actual {
    color: ${greenMui._700};
  }

  .time-cell__value--late {
    color: ${orangeMui._800};
  }

  .time-cell__value--early {
    color: ${greenMui._700};
    font-size: 14px;
    font-weight: 600;
  }

  .time-cell__value--muted {
    color: ${greyMui._600};
    font-weight: 500;
  }

  .detour-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    padding: 8px 10px;
    margin-bottom: 12px;
    background: ${redMui._50};
    border: 1px solid ${redMui._200};
    border-radius: 6px;
  }

  .detour-banner__emoji {
    flex-shrink: 0;
    font-size: 18px;
    line-height: 1;
  }

  .detour-banner__label {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 14px;
    font-weight: 700;
    color: ${redMui._800};
    line-height: 1.25;
    overflow-wrap: anywhere;
  }

  .open-ride-icon-button {
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: 22px;
    height: 22px;
    padding: 0;
    color: ${blueMui._600};
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;

    svg {
      width: 16px;
      height: 16px;
    }

    &:hover {
      color: ${blueMui._700};
      background: ${greyMui._100};
    }

    &:focus-visible {
      outline: 2px solid ${blueMui._700};
      outline-offset: 2px;
    }
  }

  .details {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    min-width: 0;
  }

  .details-bottom {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;

    .detail-row--last-update {
      grid-template-columns: minmax(0, max-content) minmax(0, 1fr) auto;
      align-items: center;
      padding-top: 8px;
      border-top: 1px solid ${greyMui._200};
    }
  }

  .detail-row {
    display: grid;
    grid-template-columns: minmax(0, max-content) minmax(0, 1fr);
    column-gap: 12px;
    align-items: baseline;
    font-size: 13px;
    line-height: 1.35;
    min-width: 0;
    width: 100%;
  }

  .detail-row__label {
    justify-self: start;
    color: ${greyMui._600};
    font-weight: 500;
    white-space: nowrap;
  }

  .detail-row__value {
    justify-self: stretch;
    min-width: 0;
    color: ${rydeColors.black};
    text-align: start;
  }

  .detail-row__value a {
    color: ${greyMui._700};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .detail-row--course .detail-row__value--course {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    max-width: 100%;
  }

  .detail-row__course-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: start;
    white-space: nowrap;
  }
`;
